import type { CatalogItem } from '@/entities/catalog-item';

// Optional "GX" prefix + one or more digits. "GX001" and "001" are equivalent.
const CODE_QUERY = /^(?:GX)?(\d+)$/;

/**
 * Code-aware pre-search for the catalog.
 *
 * A code query — `GX` + digits, or bare digits — is matched against `Code` and
 * never fuzzy-searched, so code lookups carry no title noise. The `GX` prefix
 * is optional: `GX001` and `001` produce identical results. The typed digits
 * can appear anywhere in the code's digit portion, so `70` matches
 * `GX0013870` (at the end) as well as `GX0700001` (at the start).
 *
 * @returns rows matching the normalised query (possibly empty when no code
 *   matches), or `null` when the query is not a code query and the caller
 *   should fall back to fuzzy search.
 */
export function matchCatalogCodes(items: CatalogItem[], query: string): CatalogItem[] | null {
  const match = query.trim().toUpperCase().match(CODE_QUERY);
  if (!match) return null;

  const digits = match[1];

  return items.filter(item => {
    const codeDigits = item.Code.trim().toUpperCase().replace(/^GX/, '');
    return codeDigits.includes(digits);
  });
}
